import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Form } from "antd";
import { FileNotesDocument as FileNotesDocumentIcon } from "assets/icons";
import Layout from "components/layout";
import { RegisterButton } from "components/registerButton";
import { bindActionCreators } from "redux";
import { Creators as ProcessActions } from "store/process/process-duck";
import { IDocumentList, IEditProcess, IItems } from "store/process/process-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import TokenService from "utils/auth/Token.service";
import useTranslation from "utils/hooks/useTranslation";
import YmsDrawer from "./../../../components/drawer";
import FormNewProcess from "./components/form";
import ProcessFilter from "./filter";
import ProcessGrid from "./grid/";
import { onConfirmDrawerNewProcess, onConfirmDrawerUpdateProcess } from "./process.service";
import * as S from "./styled/index.styled";

interface IPropsScreenProcess {
  isLoading?: boolean;
  isLoadingTable?: boolean;
  items?: Array<IItems>;
  totalItems?: number;
  pageIndex?: number;
  visibleDrawerNewProcess?: boolean;
  visibleDrawerEditProcess?: boolean;
  documentsList?: Array<IDocumentList>;
  searchFilter?: string;
  editProcess?: IEditProcess;
  formEditModified?: boolean;
  visibleDocuments?: boolean;
  eventInboundActive?: boolean;
  eventOutboundActive?: boolean;
  userData?: IUserData;
  allowImageCapture?: boolean;
}
interface IDispatchProps {
  processFetchRegisterAsync?: (...args) => void;
  processChangeFieldValue?: (...args) => void;
  processHideDrawerNewProcess?: (...args) => void;
  processShowDrawerNewProcess?: (...args) => void;
  processSaveNewProcessAsync?: (...args) => void;
  processFetchDocumentsAsync?: (...args) => void;
  processUpdateFilter?: (...args) => void;
  processDeleteByIdAsync?: (...args) => void;
  processUpdateProcess?: (...args) => void;
  processHideDrawerEditProcess?: (...args) => void;
  processShowDrawerEditProcess?: (...args) => void;
  processUpdateProcessAsync?: (...args) => void;
  processUpdateFormModified?: (...args) => void;
  processEventInboundActive?: (...args) => void;
  processEventOutboundActive?: (...args) => void;
  processFetchByIdAsync?: (...args) => void;
  processUpdateVisibleDocuments?: (...args) => void;
  processImageCapturesActive?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
}

type IProps = IPropsScreenProcess & IDispatchProps;

const Process = ({
  isLoading,
  items,
  totalItems,
  pageIndex,
  userData,
  documentsList,
  searchFilter,
  editProcess,
  visibleDrawerNewProcess,
  visibleDrawerEditProcess,
  formEditModified,
  eventInboundActive,
  eventOutboundActive,
  visibleDocuments,
  isLoadingTable,
  allowImageCapture,
  processUpdateFilter,
  processFetchRegisterAsync,
  processChangeFieldValue,
  processHideDrawerNewProcess,
  processShowDrawerNewProcess,
  processSaveNewProcessAsync,
  processFetchDocumentsAsync,
  processDeleteByIdAsync,
  processHideDrawerEditProcess,
  processShowDrawerEditProcess,
  processUpdateProcess,
  processUpdateProcessAsync,
  processUpdateFormModified,
  processEventInboundActive,
  processEventOutboundActive,
  processFetchByIdAsync,
  processUpdateVisibleDocuments,
  processImageCapturesActive,
  userSessionMenuItemActive,
}: IProps) => {
  const tokenService = new TokenService();
  const [form] = Form.useForm();
  const [formEdit] = Form.useForm();
  const REQUIRED_FIELDS_MESSAGE = useTranslation({ id: "required_fields_are_required" });
  const REQUIRED_SELECT_EVENT = useTranslation({ id: "necessary_to_select_a_event" });
  const REQUIRED_TO_SELECT_A_DOCUMENT = useTranslation({ id: "need_to_select_a_document" });
  const REQUIRED_SELECT_MANDATORY = useTranslation({ id: "required_select_mandatory" });
  const REQUIRED_SELECT_INBOUND = useTranslation({ id: "required_select_inbound_event_correctly" });
  const REQUIRED_SELECT_OUTBOUND = useTranslation({ id: "required_select_outbound_event_correctly" });
  const CANNOT_EQUAL_DOCUMENTS = useTranslation({ id: "cannot_add_the_same_documents" });
  const TITLE_PAGE_REGISTRATION = useTranslation({ id: "processes_list" });
  const [processIdSelected, setProcessIdSelected] = useState(null);

  const handleOnConfirmDrawerNewProcess = () => {
    onConfirmDrawerNewProcess({
      form,
      eventInboundActive,
      eventOutboundActive,
      allowImageCapture,
      processSaveNewProcessAsync,
      messageDescription: REQUIRED_FIELDS_MESSAGE,
      messageEvent: REQUIRED_SELECT_EVENT,
      visibleDocuments,
      messageDocument: REQUIRED_TO_SELECT_A_DOCUMENT,
      messageRequired: REQUIRED_SELECT_MANDATORY,
      messageEqualDocuments: CANNOT_EQUAL_DOCUMENTS,
      messageRequiredInbound: REQUIRED_SELECT_INBOUND,
      messageRequiredOutbound: REQUIRED_SELECT_OUTBOUND,
    });
  };

  const handleOnConfirmDrawerUpdateProcess = () => {
    onConfirmDrawerUpdateProcess({
      formEdit,
      documentsList,
      processIdSelected,
      eventInboundActive,
      eventOutboundActive,
      allowImageCapture,
      processUpdateProcessAsync,
      messageDescription: REQUIRED_FIELDS_MESSAGE,
      messageEvent: REQUIRED_SELECT_EVENT,
      messageDocument: REQUIRED_TO_SELECT_A_DOCUMENT,
      messageEqualDocuments: CANNOT_EQUAL_DOCUMENTS,
      visibleDocuments,
      messageRequired: REQUIRED_SELECT_MANDATORY,
      messageRequiredInbound: REQUIRED_SELECT_INBOUND,
      messageRequiredOutbound: REQUIRED_SELECT_OUTBOUND,
    });
  };

  useEffect(() => {
    userSessionMenuItemActive(MenuItemActive.PROCESS);
    tokenService.authenticateToken();
  }, []);

  useEffect(() => {
    if (editProcess?.id) {
      setProcessIdSelected(editProcess.id);
      processFetchDocumentsAsync();
    }
  }, [editProcess]);

  useEffect(() => {
    return () => {
      processUpdateFilter(null);
    };
  }, []);

  useEffect(() => {
    if (!visibleDrawerNewProcess) {
      form.resetFields();
      processEventOutboundActive(false);
      processEventInboundActive(false);
      processUpdateVisibleDocuments(false);
      processImageCapturesActive(false);
    }
  }, [visibleDrawerNewProcess]);

  useEffect(() => {
    if (!visibleDrawerEditProcess) {
      formEdit.resetFields();
      processUpdateProcess(null);
      processEventOutboundActive(false);
      processEventInboundActive(false);
      processUpdateFormModified(false);
      processUpdateVisibleDocuments(false);
    }
  }, [visibleDrawerEditProcess]);

  return (
    <Layout title={TITLE_PAGE_REGISTRATION} isLoading={isLoading} role={userData.role}>
      <S.DefaultContainer>
        <S.HeaderContainer>
          <RegisterButton id="create-process" title="new_process" dataTestId="createProcess" onClick={processShowDrawerNewProcess} />
          <ProcessFilter processUpdateFilter={processUpdateFilter} processChangeFieldValue={processChangeFieldValue} />
        </S.HeaderContainer>
        <YmsDrawer
          width="900"
          confirmbuttontxt={useTranslation({ id: "save" })}
          cancelbuttontxt={useTranslation({ id: "cancel" })}
          maskClosable={true}
          disabled={false}
          onConfirm={handleOnConfirmDrawerNewProcess}
          title={useTranslation({ id: "register_process" })}
          placement="right"
          icon={<FileNotesDocumentIcon style={S.ProcessIcon} />}
          closable={false}
          onClose={processHideDrawerNewProcess}
          open={visibleDrawerNewProcess}
          destroyOnClose={true}
        >
          <FormNewProcess
            form={form}
            documentsList={documentsList}
            processFetchDocumentsAsync={processFetchDocumentsAsync}
            visibleDrawerNewProcess={visibleDrawerNewProcess}
            visibleDrawerEditProcess={visibleDrawerEditProcess}
            eventInboundActive={eventInboundActive}
            eventOutboundActive={eventOutboundActive}
            processEventInboundActive={processEventInboundActive}
            processEventOutboundActive={processEventOutboundActive}
            processUpdateVisibleDocuments={processUpdateVisibleDocuments}
            visibleDocuments={visibleDocuments}
            allowImageCapture={allowImageCapture}
            processImageCapturesActive={processImageCapturesActive}
          />
        </YmsDrawer>
        <ProcessGrid
          form={formEdit}
          processFetchRegisterAsync={processFetchRegisterAsync}
          processChangeFieldValue={processChangeFieldValue}
          processDeleteByIdAsync={processDeleteByIdAsync}
          items={items}
          totalItems={totalItems}
          pageIndex={pageIndex}
          searchFilter={searchFilter}
          processFetchDocumentsAsync={processFetchDocumentsAsync}
          documentsList={documentsList}
          editProcess={editProcess}
          processHideDrawerEditProcess={processHideDrawerEditProcess}
          processShowDrawerEditProcess={processShowDrawerEditProcess}
          visibleDrawerEditProcess={visibleDrawerEditProcess}
          handleOnConfirmDrawerUpdateProcess={handleOnConfirmDrawerUpdateProcess}
          processUpdateFormModified={processUpdateFormModified}
          formEditModified={formEditModified}
          eventInboundActive={eventInboundActive}
          eventOutboundActive={eventOutboundActive}
          processEventInboundActive={processEventInboundActive}
          processEventOutboundActive={processEventOutboundActive}
          processFetchByIdAsync={processFetchByIdAsync}
          processUpdateVisibleDocuments={processUpdateVisibleDocuments}
          visibleDocuments={visibleDocuments}
          isLoadingTable={isLoadingTable}
          allowImageCapture={allowImageCapture}
          processImageCapturesActive={processImageCapturesActive}
        />
      </S.DefaultContainer>
    </Layout>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...ProcessActions, ...UserSessionActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoading: state.Process.isLoading,
  items: state.Process.items,
  totalItems: state.Process.totalItems,
  pageIndex: state.Process.filter.pageIndex,
  visibleDrawerNewProcess: state.Process.visibleDrawerNewProcess,
  documentsList: state.Process.documentsList,
  searchFilter: state.Process.searchFilter,
  editProcess: state.Process.editProcess,
  visibleDrawerEditProcess: state.Process.visibleDrawerEditProcess,
  formEditModified: state.Process.formEditModified,
  visibleDocuments: state.Process.visibleDocuments,
  eventInboundActive: state.Process.eventInboundActive,
  eventOutboundActive: state.Process.eventOutboundActive,
  userData: state.UserSession.userData,
  isLoadingTable: state.Process.isLoadingTable,
  allowImageCapture: state.Process.allowImageCapture,
});

export default connect(mapStateToProps, mapDispatchToActions)(Process);
